import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB') : '—');

const summarizeFilter = (f) => {
    if (!f) return '—';
    const parts = [];
    if (f.role) parts.push(`role: ${f.role}`);
    if (f.yearGroup) parts.push(`year: ${f.yearGroup}`);
    if (f.subject) parts.push(`subject: ${f.subject}`);
    if (f.q) parts.push(`search: "${f.q}"`);
    if (Array.isArray(f.extraEmails) && f.extraEmails.length) {
        parts.push(`extras: ${f.extraEmails.length}`);
    }
    return parts.length ? parts.join(' · ') : '—';
};

export default function SentEmailsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/emails/sent`,
                authHeaders()
            );
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openDetail = async (id) => {
        setDetailLoading(true);
        setDetail({ _id: id });
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/emails/sent/${id}`,
                authHeaders()
            );
            setDetail(data);
        } catch {
            setDetail(null);
            setError('Failed to load detail.');
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Sent emails</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Audit log of every bulk email sent from the composer. Each row is one
                        send (not one recipient).
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> This is the send log — every bulk email you've fired from the composer.<br />
                            <strong>2.</strong> Click a row to see the filter snapshot (who was targeted), the exact recipients list, and the delivery status per address.<br />
                            <strong>3.</strong> Useful for auditing "did the Trustpilot email actually reach Ella's parent last month?".<br />
                            <strong>4.</strong> Nothing is editable here — logs are append-only.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 180 }}>Sent at</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell sx={{ width: 110 }}>Recipients</TableCell>
                                <TableCell>Filter</TableCell>
                                <TableCell align="right" sx={{ width: 60 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((s) => (
                                <TableRow key={s._id}>
                                    <TableCell>{fmtDate(s.createdAt)}</TableCell>
                                    <TableCell>{s.subject}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Chip
                                                size="small"
                                                label={`${s.sent}/${s.requested}`}
                                                color={s.failed ? 'warning' : 'success'}
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {summarizeFilter(s.filterSnapshot)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openDetail(s._id)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No emails sent yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {detail?.subject || 'Sent email'}
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        detail && (
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Sent at</Typography>
                                    <Typography>{fmtDate(detail.createdAt)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Result</Typography>
                                    <Typography>
                                        {detail.sent} of {detail.requested} delivered
                                        {detail.failed ? ` · ${detail.failed} failed` : ''}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Filter</Typography>
                                    <Typography>{summarizeFilter(detail.filterSnapshot)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Recipients ({detail.recipients?.length || 0})
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 0.5,
                                            maxHeight: 160,
                                            overflowY: 'auto',
                                            p: 1,
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: 13,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {(detail.recipients || []).join('\n') || '—'}
                                    </Box>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Body</Typography>
                                    <Box
                                        sx={{ mt: 0.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                        dangerouslySetInnerHTML={{ __html: detail.htmlBody || '' }}
                                    />
                                </Box>
                            </Stack>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}